import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { MOCK_ACTIONS } from '../data/mockData';

const { FiSearch, FiFilter, FiCheckCircle, FiCircle, FiClock } = FiIcons;

export default function ActionItems() {
  const [filter, setFilter] = useState('all');

  const filteredActions = MOCK_ACTIONS.filter(action => {
    if (filter === 'all') return true;
    return action.status === filter;
  });

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Action Stream</h1>
          <p className="text-gray-400 text-sm mt-1">Track and manage tasks extracted from all meetings.</p>
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          <div className="glass-panel flex items-center px-3 py-2 rounded-lg flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="text-gray-400 mr-2" />
            <input type="text" placeholder="Search tasks..." className="bg-transparent border-none outline-none text-sm text-white w-full" />
          </div>
          <button className="glass-panel p-2 rounded-lg text-gray-400 hover:text-cyan-400 transition-colors">
            <SafeIcon icon={FiFilter} />
          </button>
        </div>
      </div>

      {/* Kanban-style or List layout tabs */}
      <div className="flex gap-4 mb-6 border-b border-matrix-border/30 pb-2">
        {['all', 'pending', 'in-progress', 'completed'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`text-sm font-mono uppercase tracking-wider px-2 py-1 transition-colors ${
              filter === status ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {status.replace('-', ' ')}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-custom space-y-3 pb-10">
        {filteredActions.map((action, i) => (
          <motion.div
            key={action.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`glass-panel p-4 rounded-xl flex flex-col md:flex-row gap-4 items-start md:items-center border-l-4 ${
              action.status === 'completed' ? 'border-l-emerald-500 opacity-60' : 
              action.status === 'in-progress' ? 'border-l-amber-500' : 'border-l-cyan-400'
            }`}
          >
            <button className="mt-1 md:mt-0 text-xl">
              {action.status === 'completed' ? (
                <SafeIcon icon={FiCheckCircle} className="text-emerald-500" />
              ) : action.status === 'in-progress' ? (
                <SafeIcon icon={FiClock} className="text-amber-500" />
              ) : (
                <SafeIcon icon={FiCircle} className="text-gray-500 hover:text-cyan-400" />
              )}
            </button>
            
            <div className="flex-1">
              <h3 className={`text-base font-medium ${action.status === 'completed' ? 'text-gray-400 line-through' : 'text-white'}`}>
                {action.task}
              </h3>
              <div className="flex items-center gap-3 mt-1 text-xs font-mono text-gray-500">
                <span className="text-violet-300 bg-violet-900/30 px-2 py-0.5 rounded truncate max-w-[200px]">
                  {action.meeting}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end mt-2 md:mt-0">
              <div className="flex flex-col items-start md:items-end">
                <span className="text-[10px] text-gray-500 uppercase">Assignee</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="w-5 h-5 rounded-full bg-violet-800 flex items-center justify-center text-[10px] text-white">
                    {action.assignee.charAt(0)}
                  </div>
                  <span className="text-sm text-gray-300">{action.assignee}</span>
                </div>
              </div>
              
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-gray-500 uppercase">Due Date</span>
                <span className={`text-sm mt-0.5 font-mono ${action.status !== 'completed' ? 'text-amber-400' : 'text-gray-400'}`}>
                  {action.due}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
        {filteredActions.length === 0 && (
          <div className="text-center py-20 text-gray-500 font-mono">
            No actions found for this filter.
          </div>
        )}
      </div>
    </div>
  );
}